// HMAC-signed unsubscribe tokens, tracked links and open pixels (spec 08 §7, spec 11 §7).
// One secret, three domains separated by the "unsub:" / "track:" / "open:" prefixes, so a
// signature from one domain can never be replayed in another.
import { base64urlDecode, base64urlEncode } from "./base64url.ts";

type Lang = "fr" | "en";

const enc = new TextEncoder();
const UNSUB = "unsub:";
const TRACK = "track:";
const OPEN = "open:";
const MAC_LEN = 32;
const MAX_PAYLOAD = 400;
const PAYLOAD_RE = /^([^|\s@"]+@[^|\s@"]+)\|(\d{1,12})$/;

const isValidIssuedAt = (n: unknown): n is number =>
  typeof n === "number" &&
  Number.isSafeInteger(n) &&
  n >= 0 &&
  n <= 999_999_999_999;

async function hmac(secret: string, data: string): Promise<Uint8Array> {
  if (!secret) throw new Error("UNSUBSCRIBE_SECRET is not configured");
  const key = await crypto.subtle.importKey(
    "raw",
    enc.encode(secret),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"],
  );
  return new Uint8Array(
    await crypto.subtle.sign("HMAC", key, enc.encode(data)),
  );
}

// Constant-time for equal lengths; the length itself is not secret (always 32 bytes).
function timingSafeEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a[i] ^ b[i];
  return diff === 0;
}

function decodeUtf8Strict(bytes: Uint8Array): string | null {
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    return null;
  }
}

/** Token = base64url(HMAC-SHA256(secret, "unsub:" + email|issued_at) ‖ utf8(email|issued_at)). Never expires (spec 08 §7). */
export async function signUnsubscribeToken(
  secret: string,
  email: string,
  issuedAt = Math.floor(Date.now() / 1000),
): Promise<string> {
  if (typeof email !== "string" || !isValidIssuedAt(issuedAt)) {
    throw new Error("unsubscribe_token_invalid_input");
  }
  const payload = `${email.trim().toLowerCase()}|${issuedAt}`;
  // Same checks as verifyUnsubscribeToken, so we never mint a token it would reject.
  if (!PAYLOAD_RE.test(payload) || enc.encode(payload).length > MAX_PAYLOAD) {
    throw new Error("unsubscribe_token_invalid_input");
  }
  const mac = await hmac(secret, UNSUB + payload);
  const body = enc.encode(payload);
  const out = new Uint8Array(mac.length + body.length);
  out.set(mac);
  out.set(body, mac.length);
  return base64urlEncode(out);
}

/**
 * Rotation (runbook rotate-secrets.md): the current secret is tried first, then `previousSecret`, so links
 * already sent keep working (CASL: an unsubscribe link must work for at least 60 days; ours never expire).
 * An empty or unset previous secret is skipped, never compared. Signing always uses the current secret.
 */
export async function verifyUnsubscribeToken(
  secret: string,
  token: string,
  previousSecret?: string,
): Promise<{ ok: true; email: string; issuedAt: number } | { ok: false }> {
  if (!secret) throw new Error("UNSUBSCRIBE_SECRET is not configured");
  if (typeof token !== "string") return { ok: false };
  const raw = base64urlDecode(token);
  if (!raw || raw.length < MAC_LEN + 5 || raw.length > MAC_LEN + MAX_PAYLOAD)
    return { ok: false };
  const payload = decodeUtf8Strict(raw.slice(MAC_LEN));
  if (payload === null) return { ok: false };
  const m = PAYLOAD_RE.exec(payload);
  if (!m) return { ok: false };
  const given = raw.slice(0, MAC_LEN);
  for (const s of [secret, previousSecret]) {
    if (s && timingSafeEqual(given, await hmac(s, UNSUB + payload)))
      return { ok: true, email: m[1], issuedAt: Number(m[2]) };
  }
  return { ok: false };
}

export async function signTrackUrl(
  secret: string,
  messageId: number,
  url: string,
): Promise<string> {
  if (!Number.isSafeInteger(messageId) || messageId < 0) {
    throw new Error("track_url_invalid_input");
  }
  return base64urlEncode(await hmac(secret, `${TRACK}${messageId}|${url}`));
}

/**
 * Strict parser for the `m` query parameter of gavion-track: plain decimal digits only
 * (no sign, whitespace, exponent, hex or fraction) and a safe integer. Pass the result,
 * not the raw string, to verifyTrackUrl.
 *
 * Security note: the raw `s` and `m` query strings are attacker-controlled spellings and
 * must never be used as dedupe or idempotency keys. Key on the parsed integer message id
 * (and the verified URL) only after verifyTrackUrl returns true.
 */
export function parseTrackMessageId(m: string | null): number | null {
  if (typeof m !== "string" || !/^\d+$/.test(m)) return null;
  const n = Number(m);
  return Number.isSafeInteger(n) ? n : null;
}

/**
 * The redirect target is only trusted when this returns true, so gavion-track is not an open redirect.
 * Rotation: the current secret is tried first, then a non-empty `previousSecret`.
 */
export async function verifyTrackUrl(
  secret: string,
  messageId: number,
  url: string,
  sig: string,
  previousSecret?: string,
): Promise<boolean> {
  if (
    !Number.isSafeInteger(messageId) ||
    typeof url !== "string" ||
    typeof sig !== "string"
  )
    return false;
  const given = base64urlDecode(sig);
  if (!given || given.length !== MAC_LEN) return false;
  // Fail closed like before rotation: an unset current secret is a config error, not a mismatch.
  if (!secret) throw new Error("UNSUBSCRIBE_SECRET is not configured");
  for (const s of [secret, previousSecret]) {
    if (
      s &&
      timingSafeEqual(given, await hmac(s, `${TRACK}${messageId}|${url}`))
    )
      return true;
  }
  return false;
}

export async function buildTrackedUrl(
  base: string,
  secret: string,
  messageId: number,
  url: string,
): Promise<string> {
  const s = await signTrackUrl(secret, messageId, url);
  return `${base}/gavion-track?m=${messageId}&u=${encodeURIComponent(url)}&s=${s}`;
}

/** GAVION open-pixel: base64url(HMAC-SHA256(secret, "open:" + messageId)). Never expires. */
export async function signOpenToken(
  secret: string,
  messageId: number,
): Promise<string> {
  if (!Number.isSafeInteger(messageId) || messageId < 0) {
    throw new Error("open_token_invalid_input");
  }
  return base64urlEncode(await hmac(secret, `${OPEN}${messageId}`));
}

/**
 * GAVION open-pixel: true only for a signature of this exact message id. Rotation like the click
 * links: the current secret first, then a non-empty `previousSecret`. Key on the parsed integer only.
 */
export async function verifyOpenToken(
  secret: string,
  messageId: number,
  sig: string,
  previousSecret?: string,
): Promise<boolean> {
  if (!Number.isSafeInteger(messageId) || typeof sig !== "string") return false;
  const given = base64urlDecode(sig);
  if (!given || given.length !== MAC_LEN) return false;
  if (!secret) throw new Error("UNSUBSCRIBE_SECRET is not configured");
  for (const s of [secret, previousSecret]) {
    if (s && timingSafeEqual(given, await hmac(s, `${OPEN}${messageId}`)))
      return true;
  }
  return false;
}

export const buildUnsubscribeUrl = (
  base: string,
  token: string,
  lang: Lang,
): string =>
  `${base}/gavion-unsubscribe?t=${encodeURIComponent(token)}&l=${lang}`;
